import type { LoginRequest, RegisterRequest, ResetPasswordRequest, AuthResponse, ResetPasswordResponse, VerificationCodeResponse } from '../types/auth'
import { API_ENDPOINTS } from './api'
import { parseJSONSafely } from '../utils/json-bigint'



// Token 管理
const TOKEN_KEY = 'auth_token'
const USER_INFO_KEY = 'user_info'

export const authService = {
  // 获取token
  getToken(): string | null {
    // 先查长期存储，再查会话存储
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY)
  },

  // 设置token
  setToken(token: string, keepLoggedIn = true): void {
    if (keepLoggedIn) {
      localStorage.setItem(TOKEN_KEY, token)
      sessionStorage.removeItem(TOKEN_KEY)
    } else {
      sessionStorage.setItem(TOKEN_KEY, token)
      localStorage.removeItem(TOKEN_KEY)
    }
  },

  // 清除token
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_INFO_KEY)
    sessionStorage.removeItem(USER_INFO_KEY)
  },

  // 获取用户信息
  getUserInfo() {
    const userInfo = localStorage.getItem(USER_INFO_KEY) || sessionStorage.getItem(USER_INFO_KEY)
    return userInfo ? parseJSONSafely(userInfo) : null
  },

  // 设置用户信息
  setUserInfo(userInfo: any, keepLoggedIn = true): void {
    const payload = JSON.stringify(userInfo)
    if (keepLoggedIn) {
      localStorage.setItem(USER_INFO_KEY, payload)
      sessionStorage.removeItem(USER_INFO_KEY)
    } else {
      sessionStorage.setItem(USER_INFO_KEY, payload)
      localStorage.removeItem(USER_INFO_KEY)
    }
  },

  // 清除用户信息缓存
  clearUserInfo(): void {
    localStorage.removeItem(USER_INFO_KEY)
    sessionStorage.removeItem(USER_INFO_KEY)
  },

  // 检查是否已登录
  isLoggedIn(): boolean {
    return !!this.getToken()
  },

  // 登录
  async login(data: LoginRequest, options?: { keepLoggedIn?: boolean }): Promise<AuthResponse> {
    try {
      const response = await fetch(API_ENDPOINTS.users.login(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.getToken() && { 'Authorization': `Bearer ${this.getToken()}` }),
        },
        body: JSON.stringify(data),
      })

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const errorText = await response.text()
        throw new Error(`服务器返回非JSON响应: ${errorText}`)
      }

      const result: AuthResponse = await response.json()

      if (result.code === 200) {
        // 登录成功，保存token和用户信息
        const keep = options?.keepLoggedIn ?? true
        this.setToken(result.data.token, keep)
        this.setUserInfo(result.data, keep)
        
        // 分析用户订阅状态（在后续组件中调用）
        return result
      } else {
        // 登录失败，抛出错误信息
        throw new Error(result.msg || '登录失败')
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('网络错误，请稍后重试')
    }
  },

  // 注册
  async register(data: RegisterRequest, options?: { keepLoggedIn?: boolean }): Promise<AuthResponse> {
    try {
      const response = await fetch(API_ENDPOINTS.users.register(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.getToken() && { 'Authorization': `Bearer ${this.getToken()}` }),
        },
        body: JSON.stringify(data),
      })

      const result: AuthResponse = await response.json()

      if (result.code === 200) {
        // 注册成功，保存token和用户信息
        const keep = options?.keepLoggedIn ?? true
        this.setToken(result.data.token, keep)
        this.setUserInfo(result.data, keep)
        return result
      } else {
        // 注册失败，抛出错误信息
        throw new Error(result.msg || '注册失败')
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('网络错误，请稍后重试')
    }
  },

  // 重置密码
  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    try {
      const response = await fetch(API_ENDPOINTS.users.resetPassword(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.getToken() && { 'Authorization': `Bearer ${this.getToken()}` }),
        },
        body: JSON.stringify(data),
      })

      const result: ResetPasswordResponse = await response.json()

      if (result.code === 200) {
        return result
      } else {
        // 重置失败，抛出错误信息
        throw new Error(result.msg || '重置密码失败')
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('网络错误，请稍后重试')
    }
  },

  // 获取验证码
  async getVerificationCode(phoneOrEmail: string): Promise<VerificationCodeResponse> {
    try {
      const response = await fetch(API_ENDPOINTS.users.getCode(phoneOrEmail), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.getToken() && { 'Authorization': `Bearer ${this.getToken()}` }),
        },
      })

      const result: VerificationCodeResponse = await response.json()

      if (result.code === 200) {
        return result
      } else {
        // 获取验证码失败，抛出错误信息
        throw new Error(result.msg || '获取验证码失败')
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('网络错误，请稍后重试')
    }
  },

  // 登出
  logout(): void {
    this.clearToken()
    // 不在这里跳转，让调用者决定如何处理
  },

  // 刷新token（如果需要的话）
  async refreshToken(): Promise<boolean> {
    try {
      const token = this.getToken()
      if (!token) {
        return false
      }

      // 这里可以添加刷新token的逻辑
      // const response = await fetch(`${API_BASE_URL}/user/refresh-token`, {
      //   method: 'POST',
      //   headers: {
      //     'Authorization': token,
      //   },
      // })
      // const result = await response.json()
      // if (result.code === 200) {
      //   this.setToken(result.data.token)
      //   return true
      // }
      
      return true
    } catch (error) {
      return false
    }
  }
}
